import { useState, useEffect } from 'react'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Calendar, X, ChevronsUpDown, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react'
import { 
  format, 
  isSameDay, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isToday
} from 'date-fns'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { useRealtimeBooking } from '@/hooks/useRealtimeBooking'
import { fetchUserById, type ChatUserInfo } from '@/lib/chatData'
import { Skeleton } from '@/components/ui/skeleton'
import { completeBooking } from '@/lib/bookingData'

// Mask first half of string for privacy
const maskFirstHalf = (str: string): string => {
  if (!str || str.length <= 2) return str
  const halfLength = Math.ceil(str.length / 2)
  const masked = '*'.repeat(halfLength)
  return masked + str.slice(halfLength)
}

interface CompleteBookingDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  chatId: string
  bookingId: string | null
  onSubmit?: (data: {
    status: 'completed' | 'no_show'
    finalPrice?: number
    actualDate?: Date
    notes?: string
  }) => void
}

type BookingStatus = 'completed' | 'no_show'

export function CompleteBookingDialog({
  isOpen,
  onOpenChange,
  bookingId,
  onSubmit
}: CompleteBookingDialogProps) {
  const [status, setStatus] = useState<BookingStatus>('completed')
  const [finalPrice, setFinalPrice] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [notes, setNotes] = useState('')
  const [showCalendar, setShowCalendar] = useState(false)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [userInfo, setUserInfo] = useState<ChatUserInfo | null>(null)
  const [loadingUser, setLoadingUser] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  // Fetch booking data
  const { booking } = useRealtimeBooking(bookingId)
  
  // Debug logging
  useEffect(() => {
    console.log('[CompleteBookingDialog] Debug info:', {
      isOpen,
      bookingId,
      booking,
      userInfo
    })
  }, [isOpen, bookingId, booking, userInfo])

  // Fetch user info when booking is loaded
  useEffect(() => {
    if (booking?.userId) {
      setLoadingUser(true)
      fetchUserById(booking.userId)
        .then(info => setUserInfo(info))
        .catch(err => console.error('Error fetching user info:', err))
        .finally(() => setLoadingUser(false))
    }
  }, [booking?.userId])

  // Initialize with booking data when dialog opens
  useEffect(() => {
    if (isOpen && booking) {
      setSelectedDate(booking.scheduledTimestamp)
      setCurrentMonth(booking.scheduledTimestamp)
    }
  }, [isOpen, booking])

  // Reset when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStatus('completed')
      setFinalPrice('')
      setSelectedDate(null)
      setNotes('')
      setShowCalendar(false)
      setShowStatusDropdown(false)
      setErrors({})
      setIsSubmitting(false)
      setShowSuccess(false)
    }
  }, [isOpen])

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setShowCalendar(false)
  }

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    if (status === 'completed') {
      const price = finalPrice ? parseFloat(finalPrice) : null
      if (price !== null && (isNaN(price) || price < 0)) {
        newErrors.finalPrice = 'Please enter a valid price'
      }

      if (!selectedDate) {
        newErrors.date = 'Please select a date'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !bookingId) return

    try {
      setIsSubmitting(true)

      const data = {
        status,
        ...(status === 'completed' && {
          finalPrice: finalPrice ? parseFloat(finalPrice) : undefined,
          actualDate: selectedDate || undefined,
        }),
        notes: notes || undefined
      }

      // Call the API to update the booking
      await completeBooking(bookingId, data)

      // Show success state
      setShowSuccess(true)

      // Call the callback
      onSubmit?.(data)

      // Close after a brief delay
      setTimeout(() => {
        onOpenChange(false)
      }, 1500)
    } catch (error) {
      console.error('Error completing booking:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to complete booking. Please try again.' })
      setIsSubmitting(false)
    }
  }

  const getUserDisplayName = () => {
    if (!userInfo) return 'Client'
    
    // If user has Instagram, show that as primary and censor it
    if (userInfo.instagram) {
      const instagramHandle = userInfo.instagram.startsWith('@') ? userInfo.instagram.substring(1) : userInfo.instagram
      return maskFirstHalf(instagramHandle)
    }
    
    // If no Instagram but has email, show masked email as primary
    if (userInfo.email) {
      const [emailName, domain] = userInfo.email.split('@')
      return `${maskFirstHalf(emailName)}@${domain}`
    }
    
    // Fallback
    return userInfo.displayName || 'Client'
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[60] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
          onClick={(e) => e.stopPropagation()}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-[61] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground">
              Complete Booking
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="p-2 -mr-2"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-100px)]" onClick={(e) => e.stopPropagation()}>
            <div className="space-y-5" onClick={(e) => e.stopPropagation()}>
              {/* Client Info */}
              <div className="flex items-center gap-3 pb-3 border-b border-input">
                {loadingUser ? (
                  <>
                    <Skeleton className="w-10 h-10 rounded-md flex-shrink-0" />
                    <Skeleton className="h-5 w-32" />
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {userInfo?.avatarUrl ? (
                        <img
                          src={userInfo.avatarUrl}
                          alt={getUserDisplayName()}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-foreground font-semibold text-sm">
                          {getUserDisplayName().charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground text-sm">{getUserDisplayName()}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Status and Price Row */}
              <div className="grid grid-cols-2 gap-3">
                {/* Status Dropdown */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Booking Status
                  </Label>
                  <div className="relative">
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-between text-left font-normal",
                        errors.status && "border-destructive"
                      )}
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      type="button"
                    >
                      <span className={cn(
                        status === 'completed' && 'text-green-600',
                        status === 'no_show' && 'text-red-600'
                      )}>
                        {status === 'completed' ? 'Completed' : 'No Show'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 opacity-50" />
                    </Button>
                    {showStatusDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-input rounded-md shadow-lg z-50">
                        <button
                          onClick={() => {
                            setStatus('completed')
                            setShowStatusDropdown(false)
                          }}
                          className={cn(
                            "w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors text-green-600",
                            status === 'completed' && 'bg-background font-semibold'
                          )}
                        >
                          Completed
                        </button>
                        <button
                          onClick={() => {
                            setStatus('no_show')
                            setShowStatusDropdown(false)
                          }}
                          className={cn(
                            "w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors text-red-600",
                            status === 'no_show' && 'bg-background font-semibold'
                          )}
                        >
                          No Show
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Price - Only show if status is completed */}
                {status === 'completed' && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">
                      Final Price (€)
                    </Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      placeholder="e.g. 200"
                      value={finalPrice}
                      onChange={(e) => setFinalPrice(e.target.value)}
                      className={cn(errors.finalPrice && "border-destructive")}
                    />
                    {errors.finalPrice && (
                      <p className="text-xs text-destructive mt-1">{errors.finalPrice}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">
                  Notes / Feedback
                </Label>
                <Textarea
                  placeholder="Went really well! But..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className={cn("resize-none", errors.notes && "border-destructive")}
                />
              </div>

              {/* Date - Only show if status is completed */}
              {status === 'completed' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">
                    Date
                  </Label>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      errors.date && "border-destructive"
                    )}
                    onClick={() => setShowCalendar(true)}
                    type="button"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                  </Button>
                  {errors.date && (
                    <p className="text-xs text-destructive mt-1">{errors.date}</p>
                  )}
                </div>
              )}

              {/* Error Message */}
              {errors.submit && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{errors.submit}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex flex-col items-center gap-3 pt-4">
                {showSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full"
                  >
                    <Button
                      disabled
                      className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] bg-green-500 hover:bg-green-500 text-white"
                      size="lg"
                    >
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Done!
                    </Button>
                  </motion.div>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (status === 'completed' && !finalPrice)}
                    className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)] bg-primary text-white"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>

      {/* Calendar Popup Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogPortal>
          <DialogPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-[70] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
            onClick={(e) => e.stopPropagation()}
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-[71] translate-x-[-50%] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] p-0 overflow-hidden",
              "w-[90vw] max-w-[400px]"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Calendar Content */}
            <div className="p-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold mb-4">Select Date</h3>
              
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-3">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
                  className="p-1 rounded-full hover:bg-muted"
                  type="button"
                >
                  <ChevronLeft className="w-4 h-4 text-foreground" />
                </motion.button>
                
                <h2 className="text-sm font-medium text-foreground">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
                  className="p-1 rounded-full hover:bg-muted"
                  type="button"
                >
                  <ChevronRight className="w-4 h-4 text-foreground" />
                </motion.button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {/* Day names */}
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                    {day}
                  </div>
                ))}
                
                {/* Padding days */}
                {(() => {
                  const monthStart = startOfMonth(currentMonth)
                  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
                  return Array.from({ length: startPadding }, (_, i) => (
                    <div key={`pad-${i}`} className="aspect-square" />
                  ))
                })()}
                
                {/* Actual days */}
                {eachDayOfInterval({ 
                  start: startOfMonth(currentMonth), 
                  end: endOfMonth(currentMonth) 
                }).map(date => {
                  const isSelected = selectedDate && isSameDay(date, selectedDate)
                  
                  return (
                    <motion.button
                      key={date.toISOString()}
                      whileHover={{ scale: 0.95 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDateSelect(date)}
                      type="button"
                      className={cn(
                        "aspect-square rounded-md flex items-center justify-center text-xs transition-colors relative",
                        isSelected && "bg-primary text-primary-foreground",
                        !isSelected && "hover:bg-muted",
                        isToday(date) && !isSelected && "border border-primary",
                        !isSameMonth(date, currentMonth) && "text-muted-foreground opacity-50",
                        !isSelected && "text-foreground"
                      )}
                    >
                      {format(date, 'd')}
                    </motion.button>
                  )
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCalendar(false)}
                  className="flex-1"
                  type="button"
                >
                  Done
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </Dialog>
  )
}

